import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { baseURL } from '../config';
import AuthContext from '../Context/AuthContext';
import Searchbar from './Searchbar';
import LoadingAnimation from './LoadingAnimation';
import Popup from './Popup';
import ButtonSolid from './ButtonSolid';
import ButtonOutline from './ButtonOutline';
import AssetDropzone, { UPLOAD_ASSET_TYPES } from './AssetDropzone';
import AssetLink from './AssetLink';
import { CloseIcon, AccordionArrow } from './Icons';
import { STAGE_LABEL, showsProgress, uploadNewAsset, replaceAsset as replaceAssetDirectToS3 } from '../utils/assetUpload';
import { canHoldAssetType } from '../utils/assetTypeRules';

const PAGE_SIZE = 50;
const ASSET_TYPES = [
    'Parts Pages',
    'Manuals',
    'Electrical Schematics',
    'Pneumatic Schematics',
    'Tutorial Videos',
    'Filler Controller Manuals',
    'Control Package',
];
const CONTROL_PACKAGE_TYPE = 'Control Package';

// Same per-type caps as the assembly-level Electrical/Pneumatic Schematic pickers.
const TYPE_MAX_SELECT = {
    'Electrical Schematics': 3,
    'Pneumatic Schematics': 1,
};

function AssetSelector({ label, selected, onAdd, onRemove, onReplaced, disabled, assemblyType = '', allowControlPackage = false, onRegenerateControlPackage, regenerateBlockedReason = '' }) {
    const { token, logout } = useContext(AuthContext);
    // Control Package assets are generated for Control Package assemblies only.
    // Some asset types only belong on certain assembly types (e.g. Electrical Schematics
    // on "Electrical Schematic" assemblies), so only the ones this assembly can hold show.
    const visibleTypes = ASSET_TYPES.filter((type) => (type !== CONTROL_PACKAGE_TYPE || allowControlPackage) && canHoldAssetType(assemblyType, type));
    const [expandedType, setExpandedType] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [reloadFlag, setReloadFlag] = useState(0);
    const [assetData, setAssetData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [fetchingAssetData, setFetchingAssetData] = useState(false);
    const observer = useRef();
    const debounceRef = useRef(null);
    const searchContainerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                setSearchInput('');
                setSearch('');
                setPage(1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- Upload a brand-new asset and attach it to this assembly ---
    const [showAddModal, setShowAddModal] = useState(false);
    const [pendingNewFile, setPendingNewFile] = useState(null); // { file, type }
    const [addUploading, setAddUploading] = useState(false);
    const [addProgress, setAddProgress] = useState(0);
    const [addStage, setAddStage] = useState(null); // 'checking' | 'uploading' | 'finalizing'
    const addAbortRef = useRef(null);

    // --- Replace the file behind an already-selected asset ---
    const [replaceTarget, setReplaceTarget] = useState(null); // the selected asset item
    const [replaceUsage, setReplaceUsage] = useState({ loading: false, count: 0 });
    const [replacePendingFile, setReplacePendingFile] = useState(null); // File
    const [replaceDuplicate, setReplaceDuplicate] = useState(null); // matching existing asset
    const [replaceUploading, setReplaceUploading] = useState(false);
    const [replaceProgress, setReplaceProgress] = useState(0);
    const [replaceStage, setReplaceStage] = useState(null);
    const replaceAbortRef = useRef(null);

    useEffect(() => {
        if (!expandedType || !search.trim()) {
            setAssetData([]);
            setTotalPages(1);
            return;
        }
        // Ignore a response that lands after the search or the open type has changed.
        let stale = false;
        const fetchAssetData = async () => {
            try {
                setFetchingAssetData(true);
                const response = await axios.get(`${baseURL}/asset/getAllAssets`, {
                    headers: { 'ngrok-skip-browser-warning': 'any', 'x-access-token': token },
                    params: { page, limit: PAGE_SIZE, search, filter: expandedType },
                });
                if (stale) return;
                const results = response.data.data.assetData || [];
                if (page === 1) {
                    setAssetData(results);
                } else {
                    setAssetData((prev) => [...prev, ...results]);
                }
                setTotalPages(response.data.data.pagination.totalPages);
            } catch (error) {
                if (!stale && error.response?.status === 401) logout();
            } finally {
                if (!stale) setFetchingAssetData(false);
            }
        };
        fetchAssetData();
        return () => {
            stale = true;
        };
    }, [expandedType, search, page, reloadFlag, token, logout]);

    const clearSearch = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setSearchInput('');
        setSearch('');
        setPage(1);
    };

    // One type is open at a time, and its search belongs to it - so switching clears the search.
    const toggleType = (type) => {
        clearSearch();
        setAssetData([]);
        setExpandedType((prev) => (prev === type ? null : type));
    };

    const handleSearchChange = (value) => {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(value);
            setPage(1);
        }, 500);
    };

    const infiniteScrollRef = (node) => {
        if (fetchingAssetData) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && page < totalPages) {
                setPage((prev) => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    };

    const isSelected = (item) => selected.some((s) => s._id === item._id);

    const handleToggle = (item) => {
        if (disabled) return;
        if (isSelected(item)) {
            onRemove(item);
            return;
        }
        const max = TYPE_MAX_SELECT[item.type];
        if (max) {
            const itemsOfType = selected.filter((s) => s.type === item.type);
            if (max === 1) {
                if (itemsOfType.length > 0) onRemove(itemsOfType[0]);
                onAdd(item);
                return;
            }
            if (itemsOfType.length >= max) {
                toast.error(`You can only select up to ${max} ${item.type} assets`);
                return;
            }
        }
        onAdd(item);
    };

    // --- Add new asset ---

    const openAddModal = () => setShowAddModal(true);

    const closeAddModal = () => {
        if (addUploading) return;
        setShowAddModal(false);
        setPendingNewFile(null);
        setAddProgress(0);
        setAddStage(null);
    };

    const cancelAddUpload = () => {
        addAbortRef.current?.abort();
    };

    const submitNewUpload = async () => {
        if (!pendingNewFile) return;
        const { file, type } = pendingNewFile;

        const controller = new AbortController();
        addAbortRef.current = controller;
        try {
            setAddUploading(true);
            setAddProgress(0);
            const result = await uploadNewAsset({
                file,
                type,
                token,
                signal: controller.signal,
                onStage: setAddStage,
                onProgress: setAddProgress,
            });
            toast.success(result.duplicate ? 'An identical file already exists — reusing it' : 'Asset uploaded successfully');
            onAdd(result.asset);
            setShowAddModal(false);
            setPendingNewFile(null);
            if (!result.duplicate && search.trim()) {
                setPage(1);
                setReloadFlag((prev) => prev + 1);
            }
        } catch (error) {
            if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
                toast.error('Upload cancelled');
            } else if (error.response?.status === 401) {
                logout();
            } else {
                toast.error(error.response?.data?.message || error.message || 'Error uploading asset');
            }
        } finally {
            setAddUploading(false);
            setAddProgress(0);
            setAddStage(null);
            addAbortRef.current = null;
        }
    };

    // --- Replace existing asset ---

    const openReplace = async (item) => {
        setReplaceTarget(item);
        setReplacePendingFile(null);
        setReplaceDuplicate(null);
        setReplaceProgress(0);
        setReplaceUsage({ loading: true, count: 0 });
        try {
            const response = await axios.get(`${baseURL}/asset/${item._id}/usage`, {
                headers: { 'x-access-token': token },
            });
            setReplaceUsage({ loading: false, count: (response.data.data || []).length });
        } catch (error) {
            if (error.response?.status === 401) logout();
            setReplaceUsage({ loading: false, count: 0 });
        }
    };

    const closeReplace = () => {
        if (replaceUploading) return;
        setReplaceTarget(null);
        setReplacePendingFile(null);
        setReplaceDuplicate(null);
        setReplaceProgress(0);
        setReplaceStage(null);
    };

    const cancelReplaceUpload = () => {
        replaceAbortRef.current?.abort();
    };

    const submitReplace = async () => {
        if (!replacePendingFile || !replaceTarget) return;
        const file = replacePendingFile;

        const controller = new AbortController();
        replaceAbortRef.current = controller;
        try {
            setReplaceUploading(true);
            setReplaceProgress(0);
            const result = await replaceAssetDirectToS3({
                file,
                assetId: replaceTarget._id,
                type: replaceTarget.type,
                token,
                signal: controller.signal,
                onStage: setReplaceStage,
                onProgress: setReplaceProgress,
            });
            if (result.duplicate) {
                setReplaceDuplicate(result.asset);
            } else {
                toast.success('Asset replaced successfully');
                onReplaced(result.asset);
                closeReplace();
            }
        } catch (error) {
            if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
                toast.error('Replace cancelled');
            } else if (error.response?.status === 401) {
                logout();
            } else {
                toast.error(error.response?.data?.message || error.message || 'Error replacing asset');
            }
        } finally {
            setReplaceUploading(false);
            setReplaceStage(null);
            replaceAbortRef.current = null;
        }
    };

    const useExistingInstead = () => {
        onRemove(replaceTarget);
        onAdd(replaceDuplicate);
        closeReplace();
    };

    return (
        <div className='bg-white shadow-md rounded-lg p-4 md:p-6 mb-5'>
            <div className='flex justify-between items-center mb-4'>
                <p className='text-xl font-medium text-gray-700'>{label}</p>
                <div className='flex items-center gap-3'>
                    <p className='text-sm text-gray-500'>{selected.filter((item) => visibleTypes.includes(item.type)).length} selected</p>
                    <ButtonOutline buttonText='+ Upload New' disabled={disabled} onClick={openAddModal} />
                </div>
            </div>
            <div className='border border-gray-200 rounded-md'>
                {visibleTypes.map((type) => {
                    const itemsOfType = selected.filter((item) => item.type === type);
                    const isOpen = expandedType === type;
                    return (
                        <div key={type} className='border-b border-dashed last:border-b-0'>
                            <button
                                type='button'
                                onClick={() => toggleType(type)}
                                className='w-full flex justify-between items-center gap-2 p-3 text-left hover:bg-slate-50'
                            >
                                <span className='font-medium'>{type}</span>
                                <span className='flex items-center gap-2 shrink-0'>
                                    <span className={`text-sm ${TYPE_MAX_SELECT[type] && itemsOfType.length >= TYPE_MAX_SELECT[type] ? 'text-orange-500 font-medium' : 'text-gray-400'}`}>
                                        {TYPE_MAX_SELECT[type] ? `${itemsOfType.length} / ${TYPE_MAX_SELECT[type]}` : itemsOfType.length}
                                    </span>
                                    <span className={`transition-transform duration-200 ${!isOpen ? 'rotate-180' : ''}`}>
                                        <AccordionArrow />
                                    </span>
                                </span>
                            </button>
                            {isOpen && (
                                <div className='px-3 pb-3'>
                                    {type === CONTROL_PACKAGE_TYPE && onRegenerateControlPackage && (
                                        <div className='flex flex-wrap justify-between items-center gap-3 bg-slate-50 border border-slate-200 rounded-md p-3 mb-3'>
                                            <p className={`text-sm flex-1 min-w-0 ${regenerateBlockedReason ? 'text-orange-500' : 'text-gray-500'}`}>
                                                {regenerateBlockedReason || 'Merges the PDFs of the linked embedded, electrical and pneumatic assemblies into one file and saves it here.'}
                                            </p>
                                            <ButtonOutline
                                                buttonText='Re-generate'
                                                disabled={disabled || !!regenerateBlockedReason}
                                                onClick={onRegenerateControlPackage}
                                            />
                                        </div>
                                    )}
                                    {type !== CONTROL_PACKAGE_TYPE && (
                                        <div ref={searchContainerRef}>
                                            <Searchbar
                                                fullWidth
                                                value={searchInput}
                                                disabled={disabled}
                                                placeholder={`Search ${type}...`}
                                                onChange={(e) => handleSearchChange(e.target.value)}
                                                className='mb-2'
                                            />
                                            {search.trim() && (
                                                <div className='border border-gray-200 rounded-md max-h-72 overflow-y-auto mb-3'>
                                                    {assetData.length === 0 && !fetchingAssetData && (
                                                        <p className='text-gray-400 p-3 text-sm'>{`No ${type} found for '${search}'`}</p>
                                                    )}
                                                    {assetData.map((item, index) => (
                                                        <div
                                                            key={item._id}
                                                            ref={index === assetData.length - 1 ? infiniteScrollRef : null}
                                                            onClick={() => handleToggle(item)}
                                                            className='flex justify-between items-center gap-3 border-b border-dashed p-3 cursor-pointer hover:bg-slate-50'
                                                        >
                                                            <div className='flex items-center gap-3 min-w-0'>
                                                                <input
                                                                    type='checkbox'
                                                                    readOnly
                                                                    checked={isSelected(item)}
                                                                    disabled={disabled}
                                                                    className='h-5 w-5 accent-orange-400 shrink-0'
                                                                />
                                                                <p className='text-sm truncate'>{item.name}</p>
                                                            </div>
                                                            <AssetLink
                                                                asset={item}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className='shrink-0 text-orange-500 hover:text-orange-700 text-sm'
                                                            >
                                                                View
                                                            </AssetLink>
                                                        </div>
                                                    ))}
                                                    {fetchingAssetData && <LoadingAnimation />}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <p className='text-sm font-medium text-gray-500 mt-3 mb-2'>Linked {type}</p>
                                    {itemsOfType.length === 0 && (
                                        <p className='text-gray-400 text-sm p-2'>
                                            {type === CONTROL_PACKAGE_TYPE ? 'No control package generated yet' : 'Nothing selected'}
                                        </p>
                                    )}
                                    {itemsOfType.map((item, itemIndex) => (
                                        <div key={`${item._id}-${itemIndex}`} className='bg-slate-50 border border-slate-200 rounded-md p-3 mb-2 last:mb-0'>
                                            <div className='flex justify-between items-center gap-3'>
                                                <p className='text-sm truncate min-w-0'>{item.name}</p>
                                                <AssetLink asset={item} className='shrink-0 text-orange-500 hover:text-orange-700 text-sm'>View</AssetLink>
                                            </div>
                                            <div className={`flex ${type === CONTROL_PACKAGE_TYPE ? 'justify-end' : 'justify-between'} items-center gap-3 mt-2`}>
                                                {type !== CONTROL_PACKAGE_TYPE && (
                                                    <button type='button' disabled={disabled} onClick={() => openReplace(item)} className='text-blue-500 hover:text-blue-700 disabled:text-gray-300 text-sm'>Replace</button>
                                                )}
                                                <button type='button' disabled={disabled} onClick={() => onRemove(item)} className='text-red-500 hover:text-red-700 disabled:text-gray-300 text-sm'>Remove</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {showAddModal && (
                <Popup maxWidth='md:max-w-[500px]'>
                    <div className='p-2'>
                        <div className='flex justify-between items-center mb-4'>
                            <p className='text-lg font-semibold text-gray-700'>Upload New Asset</p>
                            <button
                                type='button'
                                disabled={addUploading}
                                onClick={closeAddModal}
                                className='p-2 border-2 bg-white border-gray-300 rounded-md cursor-pointer disabled:cursor-not-allowed'
                            >
                                <CloseIcon size='w-5 h-5' />
                            </button>
                        </div>
                        {!pendingNewFile ? (
                            <AssetDropzone
                                disabled={addUploading}
                                allowedTypes={UPLOAD_ASSET_TYPES.filter((type) => visibleTypes.includes(type))}
                                onFileSelected={(file, type) => setPendingNewFile({ file, type })}
                            />
                        ) : addUploading ? (
                            <div>
                                <p className='text-sm text-gray-600 mb-2'>
                                    {STAGE_LABEL[addStage] || 'Working...'} <span className='font-medium'>{pendingNewFile.file.name}</span>
                                </p>
                                {showsProgress(addStage) && (
                                    <>
                                        <div className='w-full bg-gray-200 rounded-full h-2 mb-2'>
                                            <div className='bg-orange-400 h-2 rounded-full transition-all' style={{ width: `${addProgress}%` }} />
                                        </div>
                                        <p className='text-xs text-gray-400 mb-4'>{addProgress}%</p>
                                    </>
                                )}
                                {!showsProgress(addStage) && <div className='mb-4' />}
                                <ButtonOutline buttonText='Cancel Upload' onClick={cancelAddUpload} className='w-full' />
                            </div>
                        ) : (
                            <div>
                                <p className='text-sm text-gray-600 mb-4'>
                                    Upload <span className='font-medium'>{pendingNewFile.file.name}</span> as{' '}
                                    <span className='font-medium'>{pendingNewFile.type}</span> and attach it to this assembly?
                                </p>
                                <div className='flex flex-col md:flex-row-reverse gap-4'>
                                    <ButtonSolid buttonText='Upload & Attach' onClick={submitNewUpload} className='w-full' />
                                    <ButtonOutline buttonText='Choose Different File' onClick={() => setPendingNewFile(null)} className='w-full' />
                                </div>
                            </div>
                        )}
                    </div>
                </Popup>
            )}

            {replaceTarget && (
                <Popup maxWidth='md:max-w-[500px]'>
                    <div className='p-2'>
                        <div className='flex justify-between items-center mb-4'>
                            <p className='text-lg font-semibold text-gray-700'>Replace Asset</p>
                            <button
                                type='button'
                                disabled={replaceUploading}
                                onClick={closeReplace}
                                className='p-2 border-2 bg-white border-gray-300 rounded-md cursor-pointer disabled:cursor-not-allowed'
                            >
                                <CloseIcon size='w-5 h-5' />
                            </button>
                        </div>

                        {replaceUsage.loading ? (
                            <LoadingAnimation />
                        ) : replaceDuplicate ? (
                            <div>
                                <p className='text-sm border-dashed border-2 border-yellow-500 bg-yellow-50 p-2 rounded-md mb-4'>
                                    An identical file already exists as <span className='font-medium'>"{replaceDuplicate.name}"</span> ({replaceDuplicate.type}). You can use it instead of replacing.
                                </p>
                                <div className='flex flex-col md:flex-row-reverse gap-4'>
                                    <ButtonSolid buttonText='Use Existing Instead' onClick={useExistingInstead} className='w-full' />
                                    <ButtonOutline buttonText='Cancel' onClick={closeReplace} className='w-full' />
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className='text-sm border-dashed border-2 border-red-500 bg-red-100 p-2 rounded-md mb-4'>
                                    <span className='text-red-600'>Warning:</span> "{replaceTarget.name}" is used by {replaceUsage.count} assembl{replaceUsage.count === 1 ? 'y' : 'ies'}. Replacing it updates the file for all of them.
                                </p>
                                {!replacePendingFile ? (
                                    <AssetDropzone
                                        disabled={replaceUploading}
                                        lockedType={replaceTarget.type}
                                        continueLabel='Continue to Replace'
                                        onFileSelected={(file) => setReplacePendingFile(file)}
                                    />
                                ) : replaceUploading ? (
                                    <div>
                                        <p className='text-sm text-gray-600 mb-2'>
                                            {STAGE_LABEL[replaceStage] || 'Working...'} <span className='font-medium'>{replacePendingFile.name}</span>
                                        </p>
                                        {showsProgress(replaceStage) && (
                                            <>
                                                <div className='w-full bg-gray-200 rounded-full h-2 mb-2'>
                                                    <div className='bg-orange-400 h-2 rounded-full transition-all' style={{ width: `${replaceProgress}%` }} />
                                                </div>
                                                <p className='text-xs text-gray-400 mb-4'>{replaceProgress}%</p>
                                            </>
                                        )}
                                        {!showsProgress(replaceStage) && <div className='mb-4' />}
                                        <ButtonOutline buttonText='Cancel Upload' onClick={cancelReplaceUpload} className='w-full' />
                                    </div>
                                ) : (
                                    <div className='mt-4'>
                                        <p className='text-sm text-gray-600 mb-4'>
                                            Replace <span className='font-medium'>{replaceTarget.name}</span> with{' '}
                                            <span className='font-medium'>{replacePendingFile.name}</span> for all {replaceUsage.count} assembl{replaceUsage.count === 1 ? 'y' : 'ies'}?
                                        </p>
                                        <div className='flex flex-col md:flex-row-reverse gap-4'>
                                            <ButtonSolid buttonText='Replace' onClick={submitReplace} className='w-full' />
                                            <ButtonOutline buttonText='Choose Different File' onClick={() => setReplacePendingFile(null)} className='w-full' />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default AssetSelector;
